<template>
  <v-card class="card pa-2">
    <div class="my-0 pa-0">
      <v-text-field
        label="Nombre"
        class="my-4 mx-2"
        :value="data.name"
        dense
        outlined
        readonly
        hide-details
      />
      <v-text-field
        label="Descripción"
        class="my-4 mx-2"
        :value="data.description"
        dense
        outlined
        readonly
        hide-details
      />
    </div>

    <p class="mx-2">{{ title }}</p>

    <v-form ref="form" v-model="valid" @submit.prevent="submitForm">
      <v-alert v-if="error" dense outlined type="error" class="mx-1">
        {{ alertValidate }}
      </v-alert>

      <div class="mx-2">
        <div v-for="field in sortedFields" :key="field.id" class="ma-0 pa-0">
          <v-menu
            v-if="field.type === 'time'"
            v-model="field._menu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="field.data"
                :name="field.name"
                :label="field.label"
                outlined
                dense
                v-bind="attrs"
                v-on="on"
                :rules="rules"
                placeholder="HH:mm"
                append-icon="mdi-clock-outline"
                readonly
              />
            </template>

            <v-time-picker v-model="field._tempTime" scrollable format="24hr">
              <v-btn
                text
                color="primary"
                class="v-picker__actions"
                @click="acceptTime(field)"
              >
                Aceptar
              </v-btn>
            </v-time-picker>
          </v-menu>

          <v-menu
            v-else-if="isDate(field)"
            v-model="field._menu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-x
            max-width="290px"
            min-width="290px"
            content-class="pa-0"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                v-model="field.data"
                v-bind="attrs"
                v-on="on"
                :label="field.name"
                :rules="rules"
                outlined
                dense
                append-icon="mdi-calendar"
                placeholder="DD-MM-YYYY"
                @input="onInput(field, $event)"
              />
            </template>

            <v-date-picker
              locale="es"
              first-day-of-week="1"
              @input="onPickDate(field, $event)"
            />
          </v-menu>

          <component
            v-else
            :is="getComponentName(field)"
            v-model="field.data"
            :label="field.name"
            :rules="getRules(field)"
            :items="getItemsSelect(field)"
            item-text="name"
            item-value="id"
            :min="field.min"
            :max="field.max"
            :multiple="isMultiple(field)"
            :type="getTypeInput(field)"
            outlined
            dense
          >
            <template
              v-if="
                isMultiple(field) &&
                getComponentName(field) === 'v-autocomplete'
              "
              v-slot:prepend-item
            >
              <v-list-item @click.stop="toggleSelectAll(field)">
                <v-list-item-action>
                  <v-checkbox
                    :input-value="allSelected(field)"
                    :indeterminate="
                      field.data?.length > 0 && !allSelected(field)
                    "
                    dense
                    hide-details
                    readonly
                  />
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title> Seleccionar todos </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider />
            </template>
            <template
              v-if="
                isMultiple(field) &&
                getComponentName(field) === 'v-autocomplete'
              "
              v-slot:selection="{ item, index }"
            >
              <span v-if="index === 0">
                {{ item.name }}
              </span>
              <span v-if="index === 1"> +{{ field.data.length - 1 }} </span>
            </template>
          </component>
        </div>
      </div>

      <v-switch
        class="mt-0 mx-2 pb-4 pt-0"
        hide-details
        v-model="dataReportPdf"
        label="Ocultar información adicional en pdf"
      />

      <div class="d-flex justify-space-between py-2 mx-2">
        <v-btn class="bt-exit" @click="cancel">Cancelar</v-btn>
        <v-btn type="submit" class="bt-exe" :disabled="loading">
          Ejecutar
        </v-btn>
      </div>
    </v-form>
  </v-card>
</template>

<script>
import reports from "../../store/reportes/reports";

export default {
  props: {
    parameters: Array,
    data: Object,
  },

  data() {
    return {
      items: [],
      valid: true,
      error: false,
      alertValidate: "",
      title: "Parámetros",
      cacheItems: {},
      rules: [(v) => !!v || this.$tc("editRegistrations.requerid")],
      lookupLoading: true,
    };
  },

  mounted() {
    this.initItems(this.parameters);
  },

  watch: {
    parameters: {
      immediate: true,
      handler(val) {
        this.initItems(val);
      },
    },
    items: {
      immediate: true,
      async handler(newItems) {
        if (!newItems || newItems.length === 0) {
          this.lookupLoading = false;
          return;
        }

        this.lookupLoading = true;
        const fieldsToLoad = newItems.filter(
          (f) => f.lookup || f.lookupMultiple || f.values,
        );

        if (fieldsToLoad.length === 0) {
          this.lookupLoading = false;
          return;
        }

        await Promise.all(fieldsToLoad.map((f) => this.loadItems(f)));
        this.lookupLoading = false;
      },
    },
  },

  computed: {
    dataReportPdf: {
      get() {
        return JSON.parse(localStorage.getItem("dataReport")) ?? false;
      },
      set(value) {
        localStorage.setItem("dataReport", JSON.stringify(!!value));
      },
    },

    dynamicFields() {
      if (!Array.isArray(this.sortedFields)) return [];
      return this.sortedFields;
    },

    sortedFields() {
      if (!Array.isArray(this.items)) return [];
      return [...this.items].sort((a, b) => a.order - b.order);
    },

    loading() {
      return this.lookupLoading;
    },
  },

  methods: {
    allSelected(field) {
      const items = this.getItemsSelect(field);
      if (!items?.length) return false;

      return (field.data || []).length === items.length;
    },

    toggleSelectAll(field) {
      const items = this.getItemsSelect(field);

      if (!items?.length) return;

      const ids = items.map((i) => i.id);

      if (this.allSelected(field)) {
        field.data = [];
      } else {
        field.data = [...ids];
      }
    },

    acceptTime(field) {
      field.data = field._tempTime || "";
      field._menu = false;
    },
    initItems(params) {
      if (this.isMultiple(params) && !Array.isArray(params.data)) {
        this.$set(params, "data", []);
      }

      if (!Array.isArray(params)) {
        this.items = [];
        return;
      }

      this.items = params.map((p) => ({ ...p }));

      this.items.forEach((f) => {
        if (f.type === "date") {
          this.$set(f, "_menu", false);

          if (/^\d{4}-\d{2}-\d{2}$/.test(f.data)) {
            const [y, m, d] = f.data.split("-");
            f.data = `${d}-${m}-${y}`;
          }
        }
        if (f.type === "time") {
          this.$set(f, "_menu", false);
          this.$set(f, "_tempTime", f.data || "");
        }
        if (f.type === "boolean") {
          f.data = f.defaultValue === "1"; // Convertir "1" a true y "0" a false para el checkbox
        }
      });
    },
    isDate(f) {
      return f.type === "date";
    },

    isMultiple(f) {
      return !!f.lookupMultiple?.length;
    },

    getComponentName(f) {
      if (f.type === "boolean") {
        return "v-checkbox";
      }

      if (f.lookup || f.lookupMultiple || f.values) {
        return "v-autocomplete";
      }

      return "v-text-field";
    },

    getTypeInput(f) {
      if (f.type === "number") return "number";
      if (f.type === "boolean") return "checkbox";
      return "text";
    },

    onInput(field, value) {
      let v = value.replace(/[^\d]/g, "").slice(0, 8);
      if (v.length >= 5) v = v.replace(/^(\d{2})(\d{2})(\d{0,4})$/, "$1-$2-$3");
      else if (v.length >= 3) v = v.replace(/^(\d{2})(\d{0,2})$/, "$1-$2");
      field.data = v;
    },

    onPickDate(field, value) {
      const [y, m, d] = value.split("-");
      field.data = `${d}-${m}-${y}`;
      field._menu = false;
    },

    getItemsSelect(field) {
      return this.cacheItems[field.name] || [];
    },

    async loadItems(field) {
      if (field.lookup || field.lookupMultiple) {
        const key = field.lookup || field.lookupMultiple;
        if (!reports.state.lookupDataMap.get(key)) {
          await reports.dispatch("getLookupData", key);
        }
        const items = reports.state.lookupDataMap.get(key) || [];
        this.$set(this.cacheItems, field.name, items);
      } else if (field.values) {
        const items = field.values
          .split(",")
          .map((v) => ({ id: v.trim(), name: v.trim() }));
        this.$set(this.cacheItems, field.name, items);
      }
    },

    getRules(field) {
      const rules = [];

      if (field.required) {
        rules.push(
          (v) =>
            (v !== null && v !== undefined && v !== "") || "Campo requerido",
        );
      }

      if (field.type === "number") {
        if (field.minValue !== undefined) {
          rules.push((v) => v >= field.minValue || `Mínimo ${field.minValue}`);
        }

        if (field.maxValue !== undefined) {
          rules.push((v) => v <= field.maxValue || `Máximo ${field.maxValue}`);
        }
      }

      if (field.type === "string") {
        if (field.length !== undefined) {
          rules.push(
            (v) =>
              (v && v.length <= field.length) ||
              `Máximo ${field.length} caracteres`,
          );
        }
      }

      return rules;
    },

    submitForm(e) {
      if (this.$refs.form.validate()) {
        this.handleSubmit(e.target);
      } else {
        this.error = true;
        this.alertValidate = "Campos requeridos";
        setTimeout(() => (this.error = false), 2000);
      }
    },

    cancel() {
      this.$refs.form.resetValidation();
      this.$emit("cancel");
    },

    formatDateForBackend(value) {
      if (!value) return value;

      if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
        const [d, m, y] = value.split("-");
        return `${y}-${m}-${d}`;
      }

      return value;
    },

    handleSubmit() {
      const params = this.sortedFields.map((field) => {
        let value = field.data;
        let label = field.data;

        if (field.type === "date") {
          value = this.formatDateForBackend(value);
          // label queda igual, ya está en formato DD-MM-YYYY
        }

        if (field.type === "number") {
          value = Number(value);
          label = value;
        }

        if (field.type === "boolean") {
          label = value ? "Sí" : "No";
          value = value ? "1" : "0";
        }

        if ((field.lookup || field.values) && !this.isMultiple(field)) {
          const options = this.getItemsSelect(field);
          const found = options.find(
            (i) => String(i.id) === String(field.data),
          );
          label = found ? found.name : field.data;

          const num = Number(value);
          value = isNaN(num) ? value : num;
        }

        if (this.isMultiple(field)) {
          const options = this.getItemsSelect(field);

          if (this.allSelected(field)) {
            label = "TODOS";
          } else {
            const selectedIds = Array.isArray(field.data) ? field.data : [];
            label = selectedIds
              .map((id) => {
                const found = options.find((i) => String(i.id) === String(id));
                return found ? found.name : id;
              })
              .join(", ");
          }

          value = Array.isArray(field.data)
            ? field.data.map((v) => Number(v))
            : [];
        }

        return {
          name: field.name,
          value: value ?? 0,
          label: label ?? value ?? 0,
        };
      });

      const payload = {
        reporteId: this.data.id,
        operId: this.data.operId,
        params,
      };

      this.$emit("save", payload);
    },
  },
};
</script>
<style scoped>
.bt-exit {
  color: white !important;
  background-color: #e2504c !important;
}

.bt-acept {
  background-color: #77dd77 !important;
  color: white !important;
}

.bt-exe {
  background-color: #39537a !important;
  color: white !important;
}

.card {
  max-width: 550px;
}
</style>
